/**
 * Essential security utilities for SPA + Okta
 * Only includes features that actually help in a browser environment
 */

/**
 * Minimal security configuration - only what matters for SPAs
 */
export interface SecurityConfig {
  sessionTimeoutMinutes?: number;
  allowedOrigins?: string[];
}

/**
 * Minimal session manager - only for session timeout tracking
 */
class SessionManager {
  private readonly sessions = new Map<string, { lastActivity: number }>();
  private config: Required<SecurityConfig>;

  constructor(config: SecurityConfig = {}) {
    this.config = {
      sessionTimeoutMinutes: 60,
      allowedOrigins: [window.location.origin],
      ...config,
    };

    // Clean up expired sessions periodically
    setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  /**
   * Update configuration
   */
  updateConfig(config: SecurityConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Create session entry
   */
  createSession(sessionId: string): void {
    this.sessions.set(sessionId, {
      lastActivity: Date.now(),
    });
  }

  /**
   * Update session activity
   */
  updateActivity(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (session) {
      session.lastActivity = Date.now();
      this.sessions.set(sessionId, session);
    }
  }

  /**
   * Check if session is valid (not expired)
   */
  isSessionValid(sessionId: string): boolean {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return false;
    }

    const now = Date.now();
    const sessionAge = now - session.lastActivity;
    const maxAge = this.config.sessionTimeoutMinutes * 60 * 1000;

    return sessionAge <= maxAge;
  }

  /**
   * Destroy session
   */
  destroySession(sessionId: string): void {
    this.sessions.delete(sessionId);
  }

  /**
   * Clean up expired sessions
   */
  private cleanup(): void {
    const now = Date.now();
    const maxAge = this.config.sessionTimeoutMinutes * 60 * 1000;

    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.lastActivity > maxAge) {
        this.sessions.delete(sessionId);
      }
    }
  }
}

// Singleton session manager
export const sessionManager = new SessionManager();

/**
 * ✅ ESSENTIAL: Sanitize redirect URL to prevent open redirect attacks
 * This actually helps prevent phishing attacks using your domain
 */
export const sanitizeRedirectUrl = (
  url: string,
  allowedDomains: string[] = [window.location.hostname],
): string => {
  try {
    const parsedUrl = new URL(url, window.location.origin);

    // Only allow same origin or explicitly allowed domains
    if (
      parsedUrl.origin === window.location.origin ||
      allowedDomains.includes(parsedUrl.hostname)
    ) {
      return parsedUrl.href;
    }
  } catch {
    // Invalid URL, return safe default
  }

  return window.location.origin;
};
